import { useState, useEffect } from 'react';
import { api } from '../api/client';
import type { BillableMeterV2, Tariff } from '../api/types';
import MeterTypeIcon from '../components/MeterTypeIcon';
import MeterConfigModal from '../components/MeterConfigModal';
import { Badge, Card, Alert, DataTable } from '../components/ui';
import type { DataTableColumn } from '../components/ui';

type MeterTypeFilter = 'all' | 'electricity' | 'water';

export default function MeterConfig() {
  const [meters, setMeters] = useState<BillableMeterV2[]>([]);
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<MeterTypeFilter>('all');
  const [tariffFilter, setTariffFilter] = useState<string>('all');
  const [buildingFilter, setBuildingFilter] = useState<string>('all');
  const [tenantSearch, setTenantSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [savingMeters, setSavingMeters] = useState<Record<string, boolean>>({});
  const [savedMeters, setSavedMeters] = useState<Record<string, boolean>>({});
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const [configureMeter, setConfigureMeter] = useState<BillableMeterV2 | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, typeFilter, tariffFilter, buildingFilter, tenantSearch, pageSize]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [metersRes, tariffsRes] = await Promise.all([
        api.getBillableMetersV2(),
        api.getTariffsV2(),
      ]);
      setMeters(metersRes.data);
      setTariffs(tariffsRes.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Silent refresh (no full-page spinner) — used after the TOU config modal
  // saves so the "TOU Set" indicator reflects the new register mapping.
  const refreshMeters = async () => {
    try {
      const res = await api.getBillableMetersV2();
      setMeters(res.data);
    } catch { /* keep current rows on a transient refresh failure */ }
  };

  const handleTariffChange = async (assetId: string, tariffId: string) => {
    if (!tariffId) return;
    try {
      setSavingMeters((prev) => ({ ...prev, [assetId]: true }));
      setSaveErrors((prev) => {
        const next = { ...prev };
        delete next[assetId];
        return next;
      });

      await api.setMeterTariff(assetId, tariffId);

      const selectedTariff = tariffs.find((t) => t.id === tariffId);
      setMeters((prev) =>
        prev.map((m) =>
          m.meter_asset_id === assetId
            ? { ...m, tariff_id: tariffId, tariff_name: selectedTariff?.name ?? '', has_tariff: true }
            : m
        )
      );

      setSavedMeters((prev) => ({ ...prev, [assetId]: true }));
      setTimeout(() => {
        setSavedMeters((prev) => {
          const next = { ...prev };
          delete next[assetId];
          return next;
        });
      }, 2000);
    } catch (err) {
      setSaveErrors((prev) => ({
        ...prev,
        [assetId]: err instanceof Error ? err.message : 'Failed to save',
      }));
    } finally {
      setSavingMeters((prev) => {
        const next = { ...prev };
        delete next[assetId];
        return next;
      });
    }
  };

  const truncateUuid = (uuid: string) => {
    if (uuid.length <= 13) return uuid;
    return `${uuid.slice(0, 8)}...${uuid.slice(-4)}`;
  };

  const isWaterMeter = (type: string) => type === 'GroundwaterSensorAsset';
  const meterUtility = (type: string): 'water' | 'electricity' =>
    isWaterMeter(type) ? 'water' : 'electricity';
  // tariffs from the v2 endpoint carry utility_type; fall back to name heuristic.
  const tariffUtility = (t: Tariff): 'water' | 'electricity' =>
    t.utility_type ?? (/water/i.test(t.name) ? 'water' : 'electricity');

  // Apply filters
  const filteredMeters = meters.filter((m) => {
    const matchesSearch = m.meter_name.toLowerCase().includes(search.toLowerCase()) ||
      m.meter_asset_id.toLowerCase().includes(search.toLowerCase());
    const matchesType =
      typeFilter === 'all' ||
      (typeFilter === 'water' && isWaterMeter(m.meter_type)) ||
      (typeFilter === 'electricity' && !isWaterMeter(m.meter_type));
    const matchesTariff =
      tariffFilter === 'all' ||
      (tariffFilter === 'none' && !m.has_tariff) ||
      m.tariff_id === tariffFilter;
    const matchesBuilding =
      buildingFilter === 'all' ||
      (buildingFilter === 'none' && !m.building_asset_id) ||
      m.building_asset_id === buildingFilter;
    const matchesTenant =
      tenantSearch.trim() === '' ||
      (m.tenant_name?.toLowerCase().includes(tenantSearch.trim().toLowerCase()) ?? false);
    return matchesSearch && matchesType && matchesTariff && matchesBuilding && matchesTenant;
  });

  // Building dropdown options
  const buildings = Array.from(
    new Map(
      meters
        .filter((m) => m.building_asset_id)
        .map((m) => [m.building_asset_id!, m.building_name || m.building_asset_id!] as const),
    ).entries(),
  ).sort(([, a], [, b]) => a.localeCompare(b));

  // Pagination
  const totalFiltered = filteredMeters.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedMeters = filteredMeters.slice(startIdx, startIdx + pageSize);

  // Stats
  const totalMeters = meters.length;
  const withTariff = meters.filter((m) => m.has_tariff).length;
  const withoutTariff = totalMeters - withTariff;

  // Unique tariffs assigned for filter dropdown
  const assignedTariffIds = new Set(meters.filter(m => m.tariff_id).map(m => m.tariff_id!));

  // 8 columns down to the 2-primary ceiling: Meter Name identifies the row,
  // Current Tariff is the one field this page exists to let people scan for
  // (the whole page is "assign tariffs to meters" — the two summary tiles
  // above are Tariff Assigned / No Tariff). The interactive Assign Tariff
  // <select> stays secondary rather than primary: putting a live form
  // control in the always-visible row means it must never force horizontal
  // overflow, and it already mirrors the current tariff as its selected
  // value, so nothing is lost by keeping it one tap away. Status (a redundant
  // check/cross of the same has_tariff fact) and TOU config are secondary too.
  const meterColumns: DataTableColumn<BillableMeterV2>[] = [
    {
      key: 'status',
      label: 'Status',
      priority: 'secondary',
      // LIGHT-mode AA failures, which is why the dark-mode sweeps never
      // looked at them: neither branch carried a `dark:` class, so nothing
      // flagged them. `text-green-500` (#22c55e) on `surface`/`surfaceRaised`
      // light (#ffffff) is 2.28:1 and `text-red-500` (#ef4444) is 3.76:1 —
      // both below the 4.5:1 AA floor for normal text (these glyphs are
      // text, not icons, so the 3:1 non-text allowance does not apply).
      // Routed through ui/Badge on BuildingDetail.tsx:363-370's precedent,
      // which expresses this same has-a-tariff fact as
      // success/danger tones. Badge's tones are the only fills in this app
      // whose text/bg ratios are policed in both themes by
      // src/test/contrast.test.ts, so this stops being a shade anyone has to
      // hand-check. `title` is preserved (Badge forwards it); the glyphs and
      // the has_tariff conditional are unchanged.
      render: (meter) => (
        meter.has_tariff ? (
          <Badge tone="success" title="Tariff assigned">&#10003;</Badge>
        ) : (
          <Badge tone="danger" title="No tariff">&#10007;</Badge>
        )
      ),
    },
    {
      key: 'meter',
      label: 'Meter Name',
      priority: 'primary',
      render: (meter) => (
        <div className="flex items-center gap-2">
          <MeterTypeIcon type={meter.meter_type} className="w-5 h-5 flex-shrink-0" />
          {meter.meter_name}
          {meter.is_virtual && (
            <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-100 rounded uppercase tracking-wide" title="Software-defined meter; sources configured under Virtual Meters">
              Virtual
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'assetId',
      label: 'Meter Asset ID',
      priority: 'secondary',
      render: (meter) => (
        <code className="text-xs bg-border-token px-2 py-1 rounded font-mono text-content" title={meter.meter_asset_id}>
          {truncateUuid(meter.meter_asset_id)}
        </code>
      ),
    },
    {
      key: 'buildingTenant',
      label: 'Building / Tenant',
      priority: 'secondary',
      render: (meter) => (
        <>
          {meter.building_name ? (
            <div className="text-content">{meter.building_name}</div>
          ) : (
            <div className="text-content-muted italic">Unassigned</div>
          )}
          {meter.tenant_name && (
            <div className="text-xs text-content-muted">{meter.tenant_name}</div>
          )}
        </>
      ),
    },
    {
      key: 'currentTariff',
      label: 'Current Tariff',
      priority: 'primary',
      // Third spelling of the same has-a-tariff fact, and the worst of the
      // three: `text-red-400` (#f87171) on light `surface` is 2.77:1, in a
      // PRIMARY column (visible at 375px, where this is one of only two
      // columns rendered). Same Badge routing and same BuildingDetail
      // precedent as the Status column above. Safe in a primary column
      // despite Badge's `whitespace-nowrap` — "No tariff" is 10 bounded
      // characters, not the unbounded free text the no-nowrap-in-a-primary
      // rule (AuditLogs.tsx) exists to keep out.
      // The has-a-tariff branch keeps a plain span: `text-content` is a
      // semantic token that already passes in both themes, and a tariff NAME
      // is unbounded free text that must stay wrappable here.
      render: (meter) => (
        meter.has_tariff ? (
          <span className="text-content">{meter.tariff_name}</span>
        ) : (
          <Badge tone="danger">No tariff</Badge>
        )
      ),
    },
    {
      key: 'assignTariff',
      label: 'Assign Tariff',
      priority: 'secondary',
      render: (meter) => (
        <div className="flex items-center gap-2">
          <select
            value={meter.tariff_id ?? ''}
            onChange={(e) => handleTariffChange(meter.meter_asset_id, e.target.value)}
            disabled={savingMeters[meter.meter_asset_id]}
            className="text-sm border border-border-token rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed w-full max-w-full lg:w-[300px] lg:max-w-[360px]"
          >
            <option value="">-- Select tariff --</option>
            {tariffs
              .filter((t) => tariffUtility(t) === meterUtility(meter.meter_type))
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.rate_type === 'tou' ? 'TOU' : 'Simple'})
                </option>
              ))}
          </select>
          {savingMeters[meter.meter_asset_id] && <span className="text-xs text-content-muted">Saving...</span>}
          {/* green-700, not green-600: at `text-xs` this is normal text, so
              the floor is 4.5:1 and green-600 on white is 3.30:1. (The
              green-600 KPI below at `text-2xl font-semibold` is LARGE text —
              3:1 floor — so it is a genuine pass and is left alone.) Same
              fix and same `statusOkText` token as ReportSchedulePanel's
              `sent` branch. */}
          {savedMeters[meter.meter_asset_id] && <span className="text-xs text-green-700 dark:text-green-400 font-medium">Saved</span>}
          {saveErrors[meter.meter_asset_id] && <span className="text-xs text-red-600 dark:text-red-400">{saveErrors[meter.meter_asset_id]}</span>}
        </div>
      ),
    },
    {
      key: 'touSet',
      label: 'TOU Set',
      priority: 'secondary',
      render: (meter) => (
        !isWaterMeter(meter.meter_type) ? (
          <input
            type="checkbox"
            checked={!!meter.has_tou_config}
            readOnly
            disabled
            className="rounded border-border-token text-accent cursor-default"
            title={meter.has_tou_config
              ? 'TOU registers configured (peak / standard / off-peak)'
              : 'No TOU registers configured'}
          />
        ) : (
          <span className="text-xs text-content-muted" title="TOU does not apply to water meters">—</span>
        )
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-4" />
          <p className="text-content-muted">Loading meter configuration...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Alert tone="danger" title="Error loading data">
          <p className="text-sm mt-1">{error}</p>
          <button onClick={loadData} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 transition-colors">
            Retry
          </button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-content">Meter Configuration</h1>
        <p className="text-content-muted mt-1">Assign tariffs to meters</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4">
          <p className="text-sm text-content-muted">Total Meters</p>
          <p className="text-2xl font-semibold text-content">{totalMeters}</p>
        </Card>
        {/* I1 (dark-mode contrast fix): these KPIs render inside a <Card>
            (surfaceRaised dark #292524). text-red-600 (#dc2626) is 3.14:1
            against it — fails AA. text-green-600 (#16a34a) is 4.60:1 — a
            technical pass, but a thin one this effort treats as fragile (see
            the constraints note on shipped -600 shades). dark:text-red-400
            (#f87171) is 5.48:1, dark:text-green-400 (#4ade80) is 8.71:1 —
            both comfortable margins. */}
        <Card className="p-4">
          <p className="text-sm text-content-muted">Tariff Assigned</p>
          <p className="text-2xl font-semibold text-green-600 dark:text-green-400">{withTariff}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-content-muted">No Tariff</p>
          <p className="text-2xl font-semibold text-red-600 dark:text-red-400">{withoutTariff}</p>
        </Card>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <input
          type="text"
          placeholder="Search meters..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-2 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-64"
        />

        {/* Type Filter */}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as MeterTypeFilter)}
          className="px-3 py-2 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="all">All Types</option>
          <option value="electricity">Electricity</option>
          <option value="water">Water</option>
        </select>

        {/* Tariff Filter */}
        <select
          value={tariffFilter}
          onChange={(e) => setTariffFilter(e.target.value)}
          className="px-3 py-2 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="all">All Tariffs</option>
          <option value="none">No Tariff Assigned</option>
          {tariffs
            .filter((t) => assignedTariffIds.has(t.id))
            .map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
        </select>

        {/* Building Filter */}
        <select
          value={buildingFilter}
          onChange={(e) => setBuildingFilter(e.target.value)}
          className="px-3 py-2 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="all">All Buildings</option>
          <option value="none">No Building</option>
          {buildings.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>

        {/* Tenant Search */}
        <input
          type="text"
          placeholder="Filter by tenant..."
          value={tenantSearch}
          onChange={(e) => setTenantSearch(e.target.value)}
          className="px-3 py-2 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-48"
        />

        {/* Page Size */}
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="px-3 py-2 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </select>

        {/* Result count */}
        <span className="text-sm text-content-muted ml-auto">
          {totalFiltered} meter{totalFiltered !== 1 ? 's' : ''}
          {totalFiltered !== totalMeters && ` (of ${totalMeters})`}
        </span>
      </div>

      {/* Empty State */}
      {filteredMeters.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-content-muted">
            {search || typeFilter !== 'all' || tariffFilter !== 'all' || buildingFilter !== 'all' || tenantSearch
              ? 'No meters match the current filters'
              : 'No billable meters found'}
          </p>
        </Card>
      )}

      {/* Meter Table */}
      {/* overflow-hidden clips the table's square corners to the Card's
          rounded ones (the Tenants.tsx precedent for this same DataTable
          migration); DataTable's own internal overflow-x-auto scroller still
          scrolls correctly nested inside it. */}
      {paginatedMeters.length > 0 && (
        <Card className="overflow-hidden">
          <DataTable
            data={paginatedMeters}
            columns={meterColumns}
            rowKey={(meter) => meter.meter_asset_id}
            // The pre-migration hand-rolled table headed this column "TOU".
            // Restored as "Configure" instead: this table already has a
            // visible "TOU Set" status column, and two near-identical
            // header names is worse for a screen reader than one that
            // matches the button inside the cells.
            rowActionsLabel="Configure"
            rowActions={(meter) => (
              !isWaterMeter(meter.meter_type) ? (
                <button
                  onClick={() => setConfigureMeter(meter)}
                  // `lg:min-h-0`: 44px is a TOUCH floor; in a table row it
                  // drives row height and costs rows-per-screen on desktop.
                  // Full rationale on Tenants.tsx's rowActions; escape
                  // documented at tailwind.config.js:103.
                  className="tap-target lg:min-h-0 text-xs px-2 py-1 border border-border-token rounded-md hover:bg-surface-sunken text-content-muted"
                  title="Configure TOU registers"
                >
                  Configure
                </button>
              ) : (
                <span className="text-xs text-content-muted">—</span>
              )
            )}
          />
        </Card>
      )}

      {/* Meter Config Modal */}
      {configureMeter && (
        <MeterConfigModal
          meter={configureMeter}
          candidateSources={meters.filter(
            (m) => m.meter_asset_id !== configureMeter.meter_asset_id
              && meterUtility(m.meter_type) === meterUtility(configureMeter.meter_type),
          )}
          onClose={() => setConfigureMeter(null)}
          onSaved={() => { refreshMeters(); }}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-content-muted">
            Showing {startIdx + 1}–{Math.min(startIdx + pageSize, totalFiltered)} of {totalFiltered}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <span className="px-3 py-1.5 text-sm text-content">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
